/**
 * BADGES — the emblem awarded when an achievement completes, shown in the celebration pop-up and kept in a
 * case on the player's profile.
 *
 * TWO PIECES, AND THE SPLIT IS THE POINT:
 *
 *   • A STYLE (`BadgeStyle`) is a complete emblem DESIGN with nothing on it — shape, fill, ring, bevel,
 *     glow, tier, and the rules for placing an icon or text. A couple of dozen of these, designed once in
 *     the dev designer, cover the whole game.
 *   • A BADGE REFERENCE (`BadgeRef`) is what an achievement actually stores: which style to use, and what to put
 *     on it ("plant:rose", or the text "500"). About twenty bytes.
 *
 * `resolveBadge()` combines the two into the flat object `<BadgeArt>` draws. Nothing stores a resolved badge.
 *
 * WHY. The first version gave every achievement its own full badge record, which meant 109 copies of the identical
 * tier-3 design differing only by which plant sat in the middle — ~413 bytes each, and every one of them a
 * thing that could drift out of sync with its siblings. Styles make "all tier-3 badges match" a fact of the
 * data model rather than a maintenance task, drop the per-achievement cost by ~20×, and delete a whole class of
 * bugs (orphaned badges, tombstones, badge/achievement desync) by removing the separate badge record entirely.
 * This is what makes several thousand achievements affordable to store AND to ship to every client.
 *
 * THE SHAPE SYSTEM below is unchanged and independent: parameterized SVG path builders, a few sliders each.
 *
 * The ICON is an in-game asset drawn from the PRE-BAKED png in public/icons/ (see generated/iconManifest.ts)
 * — not a live Pixi render, because the profile case draws a wall of these OUTSIDE the game.
 */

import { ICON_MANIFEST } from "../generated/iconManifest";

// ---------------------------------------------------------------------------
//  SHAPES
// ---------------------------------------------------------------------------

/** The shape families offered in the designer. Each maps a few 0..1 sliders to an SVG path. */
export type BadgeShapeId =
  | "shield" | "shieldRound" | "star" | "rosette" | "gem" | "circle" | "hexagon" | "banner" | "leaf" | "teardrop";

/** Slider values for a shape. Every field is 0..1 (or a small count) so the designer is all sliders. */
export interface BadgeShapeParams {
  /** How wide the top edge sits relative to the body (shield/banner). 0 = narrow, 1 = full width. */
  shoulder?: number;
  /** How far the bottom point drops (shield/gem/teardrop/leaf). 0 = flat base, 1 = long spike. */
  point?: number;
  /** How much the sides pull in toward the base (shield/banner/leaf). */
  taper?: number;
  /** Star/rosette point count (3..16). */
  points?: number;
  /** Star/rosette inner radius as a fraction of the outer (0.2 = spiky, 0.8 = chunky). */
  inner?: number;
  /** Whole-shape rotation in degrees (-180..180). */
  rotate?: number;
  /** Corner rounding where the family supports it (hexagon/gem/banner). */
  round?: number;
  /** CIRCLE only: the medallion's radius as a fraction of the standard 46 (0.5..1.1). A presentation medallion
   *  shrinks its disc so a star of rays (BadgeOrnament "rays") can stand out past it inside the same box. */
  scale?: number;
}

/** Defaults per family — the shape you get before touching a slider. Tuned to read well at 96px. */
export const SHAPE_DEFAULTS: Record<BadgeShapeId, BadgeShapeParams> = {
  shield:      { shoulder: 0.92, point: 0.55, taper: 0.18 },
  shieldRound: { shoulder: 0.88, point: 0.35, taper: 0.28, round: 0.5 },
  star:        { points: 5, inner: 0.42, rotate: 0 },
  rosette:     { points: 12, inner: 0.78, rotate: 0 },
  gem:         { shoulder: 0.7, point: 0.7, round: 0.15 },
  circle:      {},
  hexagon:     { rotate: 0, round: 0.12 },
  banner:      { shoulder: 0.95, point: 0.3, taper: 0.1 },
  leaf:        { point: 0.8, taper: 0.5, rotate: 0 },
  teardrop:    { point: 0.75, rotate: 0 },
};

/** Human labels for the designer's shape gallery. */
export const SHAPE_LABELS: Record<BadgeShapeId, string> = {
  shield: "Shield", shieldRound: "Round Shield", star: "Star", rosette: "Rosette", gem: "Gem",
  circle: "Medallion", hexagon: "Hexagon", banner: "Banner", leaf: "Leaf", teardrop: "Teardrop",
};

export const BADGE_SHAPE_IDS: BadgeShapeId[] = Object.keys(SHAPE_DEFAULTS) as BadgeShapeId[];

/** Clamp helper — every slider is bounded so a bad value can never produce a broken path. */
const cl = (v: number | undefined, lo: number, hi: number, dflt: number): number => {
  const n = typeof v === "number" && Number.isFinite(v) ? v : dflt;
  return Math.max(lo, Math.min(hi, n));
};

/**
 * Build the SVG path for a shape, in a 100×100 box centred on (50,50) with a ~46 unit radius.
 * Pure + deterministic: the same params always give the same path, so a badge preview and the shipped
 * badge are identical.
 */
export function badgeShapePath(shape: BadgeShapeId, p: BadgeShapeParams = {}): string {
  const P = { ...SHAPE_DEFAULTS[shape], ...p };
  const R = 46;
  const cx = 50, cy = 50;

  switch (shape) {
    case "shield":
    case "shieldRound": {
      const sh = cl(P.shoulder, 0.3, 1, 0.9) * R;          // half-width at the top
      const pt = cl(P.point, 0, 1, 0.55);                   // how far the tip drops below the body
      const tp = cl(P.taper, 0, 0.8, 0.2);                  // side pull-in
      const rnd = cl(P.round, 0, 1, shape === "shieldRound" ? 0.5 : 0);
      const top = cy - R;
      const tipY = cy + R * (0.45 + pt * 0.55);
      const midY = cy + R * 0.1;
      const midX = sh * (1 - tp * 0.5);
      // Top edge: flat, or bowed into a dome as `round` rises.
      const topEdge = rnd > 0
        ? `Q ${cx} ${top - rnd * R * 0.35} ${cx + sh} ${top}`
        : `L ${cx + sh} ${top}`;
      return [
        `M ${cx - sh} ${top}`,
        topEdge,
        `L ${cx + midX} ${midY}`,
        `Q ${cx + midX * 0.85} ${tipY - R * 0.18} ${cx} ${tipY}`,
        `Q ${cx - midX * 0.85} ${tipY - R * 0.18} ${cx - midX} ${midY}`,
        "Z",
      ].join(" ");
    }
    case "star":
    case "rosette": {
      const n = Math.round(cl(P.points, 3, 16, shape === "star" ? 5 : 12));
      const inner = cl(P.inner, 0.15, 0.95, shape === "star" ? 0.42 : 0.78) * R;
      const rot = (cl(P.rotate, -180, 180, 0) - 90) * (Math.PI / 180);
      const pts: string[] = [];
      for (let i = 0; i < n * 2; i++) {
        const r = i % 2 === 0 ? R : inner;
        const a = rot + (i * Math.PI) / n;
        pts.push(`${(cx + Math.cos(a) * r).toFixed(2)} ${(cy + Math.sin(a) * r).toFixed(2)}`);
      }
      return `M ${pts[0]} L ${pts.slice(1).join(" L ")} Z`;
    }
    case "gem": {
      // A brilliant-cut silhouette: a narrow TABLE facet on top, wide shoulders (the girdle) just below it,
      // then long pavilion facets down to the culet. `shoulder` sets the table width relative to the girdle —
      // keeping the table clearly NARROWER than the girdle is what makes this read as a cut stone instead of
      // an arrowhead, so the 0.55 factor is not decorative.
      const sh = cl(P.shoulder, 0.3, 1, 0.7) * R;
      const pt = cl(P.point, 0, 1, 0.7);
      const table = sh * 0.55;                    // top facet half-width
      const topY = cy - R * 0.72;
      const girdle = cy - R * 0.3;                // widest point, high on the stone
      const tipY = cy + R * (0.55 + pt * 0.45);
      return [
        `M ${cx - table} ${topY}`,
        `L ${cx + table} ${topY}`,
        `L ${cx + sh} ${girdle}`,
        `L ${cx} ${tipY}`,
        `L ${cx - sh} ${girdle}`,
        "Z",
      ].join(" ");
    }
    case "circle": {
      const r = R * cl(P.scale, 0.5, 1.1, 1);
      return `M ${cx - r} ${cy} a ${r} ${r} 0 1 0 ${r * 2} 0 a ${r} ${r} 0 1 0 ${-r * 2} 0 Z`;
    }
    case "hexagon": {
      const rot = (cl(P.rotate, -180, 180, 0) - 90) * (Math.PI / 180);
      const pts: string[] = [];
      for (let i = 0; i < 6; i++) {
        const a = rot + (i * Math.PI) / 3;
        pts.push(`${(cx + Math.cos(a) * R).toFixed(2)} ${(cy + Math.sin(a) * R).toFixed(2)}`);
      }
      return `M ${pts[0]} L ${pts.slice(1).join(" L ")} Z`;
    }
    case "banner": {
      // A swallowtail ribbon: flat top, tails hanging at the sides, and a deep V cut UP between them.
      // `point` drives how deep that V bites — it must travel a large share of the ribbon height or the
      // notch reads as a dent rather than a swallowtail (the first version cut only 0.15R and looked broken).
      const sh = cl(P.shoulder, 0.4, 1, 0.95) * R;
      const pt = cl(P.point, 0, 1, 0.3);
      const tp = cl(P.taper, 0, 0.6, 0.1);
      const top = cy - R * 0.85;
      const botY = cy + R * 0.95;                 // the hanging tails
      const bx = sh * (1 - tp * 0.55);            // tails pull in slightly from the top edge
      const notch = botY - R * (0.35 + pt * 0.95);
      return [
        `M ${cx - sh} ${top}`,
        `L ${cx + sh} ${top}`,
        `L ${cx + bx} ${botY}`,
        `L ${cx} ${notch}`,
        `L ${cx - bx} ${botY}`,
        "Z",
      ].join(" ");
    }
    case "leaf": {
      const pt = cl(P.point, 0.2, 1, 0.8);
      const tp = cl(P.taper, 0, 1, 0.5);
      const w = R * (1 - tp * 0.45);
      const topY = cy - R * pt;
      const botY = cy + R * pt;
      return [
        `M ${cx} ${topY}`,
        `C ${cx + w} ${cy - R * 0.35} ${cx + w} ${cy + R * 0.35} ${cx} ${botY}`,
        `C ${cx - w} ${cy + R * 0.35} ${cx - w} ${cy - R * 0.35} ${cx} ${topY}`,
        "Z",
      ].join(" ");
    }
    case "teardrop": {
      const pt = cl(P.point, 0.2, 1, 0.75);
      const topY = cy - R * (0.6 + pt * 0.4);
      const r = R * 0.8;
      return [
        `M ${cx} ${topY}`,
        `C ${cx + r} ${cy - r * 0.2} ${cx + r} ${cy + r} ${cx} ${cy + r}`,
        `C ${cx - r} ${cy + r} ${cx - r} ${cy - r * 0.2} ${cx} ${topY}`,
        "Z",
      ].join(" ");
    }
  }
}


// ---------------------------------------------------------------------------
//  BADGE STYLES — the design, held ONCE and shared by every badge that uses it
// ---------------------------------------------------------------------------

/**
 * A STYLE is the whole visual design of an emblem MINUS what sits on top of it. Shape, fill, ring, bevel,
 * glow, tier, and the rules for how an icon or text is placed — all of it, stored once.
 *
 * WHY THIS IS THE WHOLE ARCHITECTURE. An achievement does not own a badge; it NAMES a style and supplies an icon
 * (or a short text). That single indirection is what makes thousands of achievements affordable:
 *
 *   • A badge record used to cost ~413 bytes, nearly all of it a copy of the same shape/gradient/ring/glow
 *     as its 108 siblings. Naming a style instead costs ~20 bytes, and the design is paid for once.
 *   • "Every tier-3 badge across every plant looks the same" stops being 109 objects to keep in sync and
 *     becomes one style they all point at. Edit the style, every badge using it changes.
 *   • There is no separate badge table to fall out of step with the achievements, so orphaned badges, tombstones
 *     and "which achievements still use this badge?" simply do not exist as problems.
 *
 * The ICON and TEXT knobs live HERE rather than on the achievement because they are part of the design: getting
 * "500" and "10000" to both sit correctly inside one emblem is the style's job, not something to re-solve
 * per achievement. An achievement may nudge `iconScale`/`offsetY` for the rare asset that needs it (see BadgeRef).
 */
export interface BadgeStyle {
  id: string;
  name: string;
  shape: BadgeShapeId;
  shapeParams?: BadgeShapeParams;
  fill: BadgeFill;
  /** Border ring. Width 0 = no ring. */
  ring?: { colour: string; width: number };
  /** Inner bevel highlight strength, 0..1 — the cheap trick that stops a flat shape reading as a sticker. */
  bevel?: number;
  glow?: BadgeGlow;
  /** Rank tint, drawn as a thin accent tracing the shape. */
  tier?: BadgeTier;

  // ---- how content sits on this style ----
  /** Icon size as a fraction of the badge (0.15..0.9), before the shape's own usable-area correction. */
  iconScale?: number;
  /** Vertical nudge for the icon, -0.3..0.3 of the badge height. */
  iconOffsetY?: number;
  /** Text size in viewBox units (the box is 100×100). ~30 fills a medallion; ~18 suits a number under an icon. */
  textSize?: number;
  /** Text colour, and an optional dark outline so light text survives on a light fill. */
  textColour?: string;
  textOutline?: string;
  /** Vertical position of the text baseline, in viewBox units (0..100). Defaults sit it low on the body. */
  textY?: number;
  /** Letter-spacing in viewBox units — a wide emblem can carry "10000" only if the tracking tightens. */
  textTracking?: number;
  /** Auto-shrink long text so "10000" fits an emblem sized for "25". 0 = off; otherwise the width in
   *  viewBox units the text is squeezed into. */
  textFitWidth?: number;

  /**
   * An optional RIBBON — a scroll band drawn across the emblem that the text sits on.
   *
   * This exists because a WORD is a different problem from a NUMBER. The widest silhouette here offers ~65
   * units of clear span, so "FOUNDER" at a readable size either overflows the shape or has to be squeezed
   * by textFitWidth into condensed lettering that looks cheap — the opposite of what a premium reward
   * should feel like. Heraldry solved this centuries ago: put the word on a banner that is ALLOWED to
   * overhang the shield, and it reads at any size because it owns its own background.
   */
  /** A crest this style draws when the achievement does not supply its own icon. Lets a bespoke style like the
   *  Founder emblem be complete on its own — an achievement just names the style and gets the whole design. */
  defaultIcon?: string;

  /**
   * THE ASSET IS THE BADGE. When set (and the achievement names an asset icon), the emblem is not drawn at
   * all: the plant / fish / insect art itself is the badge, wrapped in coloured strokes that follow its own
   * silhouette — see BadgeOutline. The shape/fill/ring above remain as the FALLBACK for an achievement on
   * this style that has no art to wrap (text only, or an asset since removed).
   */
  outline?: BadgeOutline;

  /** PRESENTATION WORK — engraved, beaded and set layers drawn around the emblem (see BadgeOrnament). Polar
   *  about (50, `ornamentCy`), which defaults to 50: the medallion's own centre. */
  ornaments?: BadgeOrnament[];
  ornamentCy?: number;
  /** A drawn VECTOR crest (see BadgeEmblem). Takes the place of the icon, so a style can carry a crest that is
   *  not an in-game asset at all — and stays crisp at every size, which a baked png cannot. */
  emblem?: BadgeEmblem;

  ribbon?: {
    /** Band fill, and the darker tone used for the folded tails behind it. */
    colour: string;
    shade: string;
    /** Vertical centre of the band, in viewBox units. */
    y: number;
    /** Half-width of the band's flat centre. The tails extend past this. */
    halfWidth: number;
    /** Band thickness. */
    height: number;
    /** How far the tails flick out past the centre band (0 = no tails). */
    tail?: number;
    /** Upward bow of the band's centre, so it reads as cloth rather than a bar. */
    curve?: number;
    /** Optional thin outline. */
    stroke?: string;
  };
}

/**
 * STACKED STROKES around an asset's own silhouette — the species-ladder badge. `rings` run INNERMOST first,
 * and a higher rank is the rank below with one more ring added OUTSIDE it, so a Legend visibly contains every
 * rank it passed through. Each ring is a dilation of the art's alpha by the running total of the widths, with
 * a thin dark INK line outside each ring (the game's ink-line look, and what keeps two light metals apart).
 * A ring's colour may be "rainbow" — only as the OUTERMOST ring (it is drawn as a gradient through a mask).
 */
export interface BadgeOutline {
  rings: { colour: string; width: number }[];
  /** the separating ink line's colour and width, in viewBox units (the box is 100). */
  ink?: string;
  inkWidth?: number;
}

/**
 * ORNAMENT LAYERS — the goldsmith's work on a PRESENTATION badge: the star of rays behind a medal, the beaded
 * and engraved rules round its rim, the enamel field set into it, the stones set in the band. Written for the
 * Kickstarter backer badges (`founder`, `founderPatron`), but plain data, so any style may use them.
 *
 * Every layer is POLAR about one centre (50, style.ornamentCy ?? 50), in the same 100-unit box as the shapes.
 * `layer` says where it sits in the stack: "back" under the body (rays — so the medal stands ON its star),
 * "body" over the body and under the bevel/ring (fields, rules, beads — so the bevel's sheen crosses them like
 * one piece of metal), "top" over the ring and under the ribbon (stones, which stand proud of the band).
 *
 * THE INK RULE (HADES_STYLE §2): every raised piece gets a dark contour — the rays, each bead, each stone. That
 * contour is what makes gold read as raised wire at 46px rather than as a yellow smear; a metal layer without
 * it reads as paint.
 */
export type BadgeOrnament =
  /** A star of `n` faceted rays from r0 out to r1 (r2 = every other ray's length, for a long/short star). Each
   *  ray is split down its axis into a lit and a shaded facet — the cut-metal look of an order star. `alt`
   *  recolours every other ray (a two-metal star). */
  | { kind: "rays"; layer?: BadgeOrnamentLayer; n: number; r0: number; r1: number; r2?: number; colour: string; shade: string;
      alt?: { colour: string; shade: string }; ink?: string; rotate?: number }
  /** A ring of `n` beads (pearls / milled grain) of radius `size`, centred on radius `r`. */
  | { kind: "beads"; layer?: BadgeOrnamentLayer; r: number; n: number; size: number; colour: string; ink?: string; skip?: number[] }
  /** One engraved rule: a circle of radius `r` stroked `width` wide. */
  | { kind: "rule"; layer?: BadgeOrnamentLayer; r: number; width: number; colour: string }
  /** A running vine-scroll (filigree): a wave of `n` crests round radius `r`, amplitude `amp`, each crest
   *  curling back on itself. Drawn as a line, so it belongs on a broad band, never a hairline one. */
  | { kind: "scroll"; layer?: BadgeOrnamentLayer; r: number; n: number; amp: number; width: number; colour: string }
  /** A set ENAMEL FIELD: a disc of radius `r` (a radial a→b fill), its own ink edge, and an optional gold
   *  cloisonné WIRE just inside it — the enamel-pin construction the physical rewards use. */
  | { kind: "field"; layer?: BadgeOrnamentLayer; r: number; a: string; b: string; ink?: string;
      wire?: { inset: number; width: number; colour: string } }
  /** Cabochon STONES at `angles` (degrees, 0 = 3 o'clock, -90 = 12 o'clock) on radius `r`: a domed gem in a
   *  metal bezel with one catch-light. `sizes` per stone, or one `size` for all. */
  | { kind: "gems"; layer?: BadgeOrnamentLayer; r: number; angles: number[]; size: number; sizes?: number[];
      colour: string; shade: string; light: string; bezel: string; ink?: string };

export type BadgeOrnamentLayer = "back" | "body" | "top";

/**
 * A DRAWN CREST. `keys` = two keys crossed in saltire (the keys to the garden — the backers opened its gate),
 * with an optional `crown` over the crossing and a `gem` set in it. Gold is built as two hard-stopped facets
 * (lit left, shaded right, in each key's own frame) under a heavy ink contour, so it reads as cast metal, not as
 * a gradient. Centred on (50, y); `scale` 1 = keys 46 units long.
 */
export interface BadgeEmblem {
  kind: "keys";
  y?: number;
  scale?: number;
  crown?: boolean;
  gem?: string;
  metal?: string;
  shade?: string;
  ink?: string;
  /** what shows through the key bows' piercing — the field's enamel. */
  hole?: string;
}

/** Flat colour or a two-stop gradient. Kept to two stops on purpose: it covers nearly every emblem worth
 *  making and keeps the stored object small enough to read at a glance. */
export interface BadgeFill {
  kind: "flat" | "linear" | "radial";
  /** Primary colour (the only one used when `kind` is "flat"). */
  a: string;
  /** Second gradient stop. Ignored for "flat". */
  b?: string;
  /** Gradient angle in degrees (linear only). 0 = top→bottom. */
  angle?: number;
}

/** Outer glow. `pulseSpeed` drives the breathing animation; 0 disables it (a static glow). */
export interface BadgeGlow {
  colour: string;
  /** Glow spread, 0..1 (0 = none). */
  strength: number;
  /** Pulse speed in seconds per breath. 0 = no animation. */
  pulseSpeed?: number;
  /** How deep the pulse dips, 0..1. */
  pulseDepth?: number;
}

export type BadgeTier = "bronze" | "silver" | "gold" | "mythic";

/** The in-game asset drawn on a badge, referenced by kind + id (drawn from the pre-baked png). */
export interface BadgeIcon {
  kind: "plant" | "fish" | "insect" | "decor" | "pot";
  id: string;
}

/**
 * WHAT AN ACHIEVEMENT STORES. Not a badge — a reference to a style plus what goes on it. This is the ~20-byte
 * object that replaced the 413-byte badge record.
 */
export interface BadgeRef {
  /** Which BADGE_STYLES entry to draw. */
  style: string;
  /** The asset to place in the middle, as "kind:id" (e.g. "plant:rose"). Compact on purpose — it is stored
   *  once per achievement and there may be thousands. */
  icon?: string;
  /** Short text instead of / as well as an icon (e.g. "500"). */
  text?: string;
  /** Rare per-achievement overrides, for an asset whose art needs a nudge inside a particular style. */
  iconScale?: number;
  iconOffsetY?: number;
}

/** Everything <BadgeArt> needs to draw: a style with its content resolved onto it. Computed, never stored. */
export interface ResolvedBadge {
  id: string;
  name: string;
  style: BadgeStyle;
  icon?: BadgeIcon;
  text?: string;
  iconScale: number;
  iconOffsetY: number;
}

/** Parse the compact "kind:id" icon reference. Returns undefined for anything malformed or unknown. */
export function parseIconRef(ref: string | undefined): BadgeIcon | undefined {
  if (!ref) return undefined;
  const i = ref.indexOf(":");
  if (i <= 0) return undefined;
  const kind = ref.slice(0, i);
  const id = ref.slice(i + 1);
  if (!["plant", "fish", "insect", "decor", "pot"].includes(kind) || !id) return undefined;
  return { kind: kind as BadgeIcon["kind"], id };
}

/** Is this icon reference real? (Guards a badge naming an asset that has been removed from the game.) */
export function badgeIconExists(icon: BadgeIcon | undefined): boolean {
  if (!icon) return false;
  const list = ICON_MANIFEST[icon.kind] as string[] | undefined;
  return Array.isArray(list) && list.includes(icon.id);
}

/** The pre-baked PNG url for a badge icon, or null when the asset isn't baked. */
export function badgeIconUrl(icon: BadgeIcon | undefined): string | null {
  if (!badgeIconExists(icon) || !icon) return null;
  return `/icons/${icon.kind}/${icon.id}.png`;
}

// ---------------------------------------------------------------------------
//  THE STYLE LIBRARY
// ---------------------------------------------------------------------------

/**
 * SOURCE-DEFAULT STYLES. These are the emblem designs the game ships with — the "several standardized
 * shapes" everything else is built from. The live `badgeStyles` tuning key overlays this per id, so the
 * designer edits a style ONCE and every achievement using it updates.
 *
 * The five `t1`..`t5` styles are the harvest ladder, and they are deliberately a PROGRESSION: each tier is
 * a more elaborate silhouette, a richer fill and a stronger glow than the one below it, so a player can read
 * their rank from across the screen without reading a number.
 */
/** The five TIER EMBLEMS (bronze shield → mythic rosette): the t1..t5 styles, and the fallback emblem of the
 *  r1..r5 species-ladder rings. Declared apart so both can use one design. */
const T_EMBLEM: Record<"t1" | "t2" | "t3" | "t4" | "t5", BadgeStyle> = {
  t1: {
    id: "t1", name: "Tier I: Bronze Shield",
    shape: "shield", shapeParams: { shoulder: 0.9, point: 0.5, taper: 0.2 },
    fill: { kind: "linear", a: "#a9713c", b: "#5d3a1c", angle: 0 },
    ring: { colour: "#d9a066", width: 2.5 }, bevel: 0.32,
    glow: { colour: "#c07a3e", strength: 0.25, pulseSpeed: 0, pulseDepth: 0.4 },
    tier: "bronze", iconScale: 0.56, textSize: 20, textColour: "#ffe9cf", textOutline: "rgba(0,0,0,0.55)",
  },
  t2: {
    id: "t2", name: "Tier II: Silver Shield",
    shape: "shieldRound", shapeParams: { shoulder: 0.88, point: 0.38, taper: 0.26, round: 0.5 },
    fill: { kind: "linear", a: "#d3dbe4", b: "#6f7c8a", angle: 0 },
    ring: { colour: "#f2f6fa", width: 3 }, bevel: 0.4,
    glow: { colour: "#c3ccd6", strength: 0.4, pulseSpeed: 3.2, pulseDepth: 0.4 },
    tier: "silver", iconScale: 0.58, textSize: 20, textColour: "#22303c", textOutline: "rgba(255,255,255,0.5)",
  },
  t3: {
    id: "t3", name: "Tier III: Gold Gem",
    shape: "gem", shapeParams: { shoulder: 0.74, point: 0.66, round: 0.15 },
    fill: { kind: "radial", a: "#ffd76a", b: "#c9821f" },
    ring: { colour: "#fff3c9", width: 3 }, bevel: 0.48,
    glow: { colour: "#ffd76a", strength: 0.6, pulseSpeed: 2.6, pulseDepth: 0.5 },
    tier: "gold", iconScale: 0.74, iconOffsetY: -0.06, textSize: 20, textColour: "#5c3a06",
    textOutline: "rgba(255,240,200,0.65)", textY: 56, textFitWidth: 40,
  },
  t4: {
    id: "t4", name: "Tier IV: Radiant Star",
    shape: "star", shapeParams: { points: 6, inner: 0.62, rotate: 0 },
    fill: { kind: "radial", a: "#fff3c0", b: "#e0942a" },
    ring: { colour: "#fffaf0", width: 3 }, bevel: 0.5,
    glow: { colour: "#ffd76a", strength: 0.8, pulseSpeed: 2, pulseDepth: 0.6 },
    tier: "gold", iconScale: 0.78, textSize: 24, textColour: "#5c3a06",
    textOutline: "rgba(255,245,210,0.7)", textFitWidth: 46,
  },
  t5: {
    id: "t5", name: "Tier V: Mythic Rosette",
    shape: "rosette", shapeParams: { points: 11, inner: 0.86 },
    fill: { kind: "radial", a: "#e9c8ff", b: "#6a2fae" },
    ring: { colour: "#f6e6ff", width: 3 }, bevel: 0.52,
    glow: { colour: "#c77dff", strength: 1, pulseSpeed: 1.6, pulseDepth: 0.65 },
    tier: "mythic", iconScale: 0.72, textSize: 22, textColour: "#fdf4ff",
    textOutline: "rgba(40,10,60,0.6)", textFitWidth: 52,
  },

};

export const DEFAULT_BADGE_STYLES: Record<string, BadgeStyle> = {
  // ---- the tier emblems: bronze shield → silver shield → gold gem → star → mythic rosette (t1..t5) ----
  ...T_EMBLEM,

  // ---- the SPECIES LADDERS (per-plant harvest, per-fish and per-insect catch): the art is the badge ----
  // Each rank keeps every ring below it and adds its own, thickest, outside: bronze → +silver → +gold →
  // +mythic violet → +rainbow. The newest ring is always the widest, so the rank reads from its outer colour
  // at a glance; the inner ones compress so a Legend is not a fat donut. Emblem fields are the t1..t5
  // designs, kept as the fallback for an achievement on these styles with no art to wrap.
  r1: {
    ...T_EMBLEM.t1, id: "r1", name: "Rank I: Bronze Ring",
    outline: { rings: [{ colour: "#c9853f", width: 5 }], ink: "#141816", inkWidth: 1.3 },
    glow: undefined,
  },
  r2: {
    ...T_EMBLEM.t2, id: "r2", name: "Rank II: Silver Ring",
    outline: { rings: [{ colour: "#c9853f", width: 3.2 }, { colour: "#d6dee6", width: 4.6 }], ink: "#141816", inkWidth: 1.2 },
    glow: undefined,
  },
  r3: {
    ...T_EMBLEM.t3, id: "r3", name: "Rank III: Gold Ring",
    outline: { rings: [{ colour: "#c9853f", width: 2.8 }, { colour: "#d6dee6", width: 2.8 }, { colour: "#f2c14e", width: 4.6 }], ink: "#141816", inkWidth: 1.1 },
    glow: { colour: "#f2c14e", strength: 0.3, pulseSpeed: 0, pulseDepth: 0.4 },
  },
  r4: {
    ...T_EMBLEM.t4, id: "r4", name: "Rank IV: Mythic Ring",
    outline: { rings: [{ colour: "#c9853f", width: 2.5 }, { colour: "#d6dee6", width: 2.5 }, { colour: "#f2c14e", width: 2.5 }, { colour: "#b574f2", width: 4.6 }], ink: "#141816", inkWidth: 1.05 },
    glow: { colour: "#c77dff", strength: 0.55, pulseSpeed: 2.4, pulseDepth: 0.5 },
  },
  r5: {
    ...T_EMBLEM.t5, id: "r5", name: "Rank V: Rainbow Ring",
    outline: { rings: [{ colour: "#c9853f", width: 2.3 }, { colour: "#d6dee6", width: 2.3 }, { colour: "#f2c14e", width: 2.3 }, { colour: "#b574f2", width: 2.3 }, { colour: "rainbow", width: 5 }], ink: "#141816", inkWidth: 1 },
    glow: { colour: "#fff0fb", strength: 0.75, pulseSpeed: 1.8, pulseDepth: 0.6 },
  },

  // ---- general-purpose styles for the hand-authored milestone achievements ----
  leafGreen: {
    id: "leafGreen", name: "Green Leaf",
    shape: "leaf", shapeParams: { point: 0.85, taper: 0.45, rotate: 0 },
    fill: { kind: "linear", a: "#7bc24a", b: "#2f6b24", angle: 0 },
    ring: { colour: "#d8f0b0", width: 2 }, bevel: 0.4,
    glow: { colour: "#9be36a", strength: 0.45, pulseSpeed: 3, pulseDepth: 0.5 },
    tier: "bronze", iconScale: 0.6, textSize: 22, textColour: "#f4ffe6", textOutline: "rgba(0,0,0,0.5)",
  },
  shieldGreen: {
    id: "shieldGreen", name: "Green Shield",
    shape: "shield", shapeParams: { shoulder: 0.9, point: 0.5, taper: 0.2 },
    fill: { kind: "linear", a: "#4f9a34", b: "#1f4a1b", angle: 0 },
    ring: { colour: "#c3ccd6", width: 3 }, bevel: 0.35,
    glow: { colour: "#7bc24a", strength: 0.4, pulseSpeed: 2.6, pulseDepth: 0.45 },
    tier: "silver", iconScale: 0.58, textSize: 22, textColour: "#f2ffe8", textOutline: "rgba(0,0,0,0.5)",
  },
  rosettePink: {
    id: "rosettePink", name: "Pink Rosette",
    shape: "rosette", shapeParams: { points: 14, inner: 0.8 },
    fill: { kind: "radial", a: "#ef7fb0", b: "#8e2f5c" },
    ring: { colour: "#ffd9ea", width: 2 }, bevel: 0.4,
    glow: { colour: "#ff9ec9", strength: 0.5, pulseSpeed: 2.8, pulseDepth: 0.5 },
    tier: "gold", iconScale: 0.58, textSize: 22, textColour: "#fff0f7", textOutline: "rgba(60,10,35,0.5)",
  },
  gemBlue: {
    id: "gemBlue", name: "Blue Gem",
    shape: "gem", shapeParams: { shoulder: 0.72, point: 0.7, round: 0.15 },
    fill: { kind: "linear", a: "#5bb7ea", b: "#1d4f7a", angle: 0 },
    ring: { colour: "#bfe4f5", width: 3 }, bevel: 0.45,
    glow: { colour: "#5bb7ea", strength: 0.55, pulseSpeed: 2.4, pulseDepth: 0.5 },
    tier: "silver", iconScale: 0.78, iconOffsetY: -0.1, textSize: 19, textColour: "#eaf7ff",
    textOutline: "rgba(0,25,45,0.55)", textY: 80,
  },
  hexWood: {
    id: "hexWood", name: "Wood Hexagon",
    shape: "hexagon", shapeParams: { rotate: 0, round: 0.12 },
    fill: { kind: "linear", a: "#b07a43", b: "#5c3a1e", angle: 0 },
    ring: { colour: "#e8c99a", width: 3 }, bevel: 0.35,
    glow: { colour: "#f2c14e", strength: 0.4, pulseSpeed: 3, pulseDepth: 0.45 },
    tier: "bronze", iconScale: 0.62, textSize: 22, textColour: "#fff1d9", textOutline: "rgba(0,0,0,0.5)",
  },
  /**
   * THE FOUNDER MEDAL — the Kickstarter backer reward, for every backer. A PRESENTATION piece, not a tier
   * emblem: a gilt medallion standing on a sixteen-point star, a milled bead ring round a set enamel field,
   * two gilt keys crossed on the enamel, and FOUNDER on a gold ribbon across the foot.
   *
   * WHAT THE PREVIOUS VERSION GOT WRONG. It was a blue round shield with a SUNFLOWER crest — one shape and one
   * picture away from every ordinary achievement badge, and the flower was a plant icon any badge can carry.
   * Nothing about it said "precious" or "earned". This one is built the way the physical enamel pins are built
   * (gold wire, dark enamel set between it) and carries a crest nothing else in the game uses.
   *
   * THE SILHOUETTE IS THE STAR. Long rays protrude 14 units past the medal, short ones 7 — 5.2px and 2.6px at the
   * 46px profile case — so a Founder reads as an ORDER STAR from across a case of shields, gems and rosettes
   * before any detail resolves. No other style in the library has a serrated edge. `scale: 0.87` on the circle
   * is what makes that room inside the box; do not raise it without shortening r1/r2, or the glow clips.
   *
   * THE CREST IS CROSSED KEYS, not a plant: the backers are who opened the garden's gate. A key is the one
   * heraldic charge that both says that and holds its shape small — two bold bars in saltire with a pierced bow
   * at each top corner read as "crossed keys" down to 44px, where a thin instrument (the armillary, tried for
   * the old shield) vanished. Shaft 4.1 units = 1.5px at 44px: above the half-pixel floor, so it is drawn.
   *
   * THE WORD STILL RIDES A RIBBON that overhangs the medal (the heraldic answer to an eight-letter word in a
   * round frame). textSize 16 is the smallest that keeps FOUNDER above BadgeArt's 7px legibility floor at the
   * 44px Gardeners list (7.04px) — the old 15 dropped the word everywhere below 47px, so the profile case showed
   * an anonymous shield. Do not lower it.
   *
   * Palette: Founder's Gilt pot colours — green-black enamel banded in gold — so the badge and the backer pot
   * read as one set. Gold is two hard facets under a heavy ink line (HADES_STYLE §2-3), never a smooth ramp.
   */
  founder: {
    id: "founder", name: "Founder",
    shape: "circle", shapeParams: { scale: 0.87 },
    fill: { kind: "linear", a: "#f2cd68", b: "#8f5e16", angle: 0 },
    ring: { colour: "#24170a", width: 1.6 }, bevel: 0.4,
    glow: { colour: "#f2c14e", strength: 0.6, pulseSpeed: 2.8, pulseDepth: 0.45 },
    ornaments: [
      { kind: "rays", layer: "back", n: 16, r0: 30, r1: 54, r2: 47, colour: "#f4d277", shade: "#a7701d", ink: "#24170a" },
      { kind: "beads", layer: "body", r: 35.6, n: 30, size: 1.55, colour: "#f8dc8a", ink: "#3a2608" },
      { kind: "field", layer: "body", r: 31, a: "#1f5048", b: "#081a17", ink: "#24170a",
        wire: { inset: 2.6, width: 0.9, colour: "#e8c25e" } },
    ],
    emblem: { kind: "keys", y: 41, scale: 0.96, metal: "#f6d57c", shade: "#b27a22", ink: "#1e1308", hole: "#0b211e" },
    textSize: 16, textColour: "#2a1a06", textOutline: "rgba(255,240,200,0.4)", textTracking: 0.6,
    textFitWidth: 62,
    ribbon: {
      colour: "#f4cf66", shade: "#a8731f", y: 76, halfWidth: 37, height: 18,
      tail: 9, curve: 2.5, stroke: "#2a1a06",
    },
  },
  /**
   * THE FOUNDING PATRON MEDAL — the Collector tier, the top backer reward. The Founder medal's SIBLING: the same
   * build (medal on a star, bead ring, set enamel field, crossed keys, word on a ribbon) so the two read as one
   * family in a case, with every element taken a step further, so the Patron is unmistakably the senior piece:
   *
   *   • a TWO-METAL STAR of 24 rays (gold long, platinum short) where the Founder has 16 in gold alone — a denser,
   *     brighter serration, and the only silver in either medal;
   *   • a DOUBLE filigree band: beads between two engraved rules, where the Founder has the bead ring alone;
   *   • three AMETHYST stones set in the band (12 o'clock and both flanks), and a fourth in the crown;
   *   • a CROWN over the crossed keys; Void-violet enamel (the Void Orchid pin's colour) in place of green;
   *   • an ENAMEL ribbon (violet, gold-edged, gold letters) in place of the plain gold one.
   *
   * Collectors hold both medals, so the difference must survive at 46px side by side: the silver-gold
   * alternation of the star and the violet field are the two reads that do (the crown and stones are 96px+
   * detail). PATRON is six letters, so it sets larger (17) than FOUNDER on the same ribbon width.
   */
  founderPatron: {
    id: "founderPatron", name: "Founding Patron",
    shape: "circle", shapeParams: { scale: 0.89 },
    fill: { kind: "linear", a: "#f4d36f", b: "#8a5814", angle: 0 },
    ring: { colour: "#1c1028", width: 1.6 }, bevel: 0.42,
    glow: { colour: "#c9a2ff", strength: 0.72, pulseSpeed: 2.2, pulseDepth: 0.5 },
    ornaments: [
      { kind: "rays", layer: "back", n: 24, r0: 30, r1: 55, r2: 48.5, colour: "#f4d277", shade: "#a26c1a",
        alt: { colour: "#f0f2f5", shade: "#98a1ae" }, ink: "#1c1028" },
      { kind: "rule", layer: "body", r: 39, width: 0.9, colour: "#6b4510" },
      { kind: "beads", layer: "body", r: 36.2, n: 32, size: 1.45, colour: "#fae2a0", ink: "#3a2608", skip: [-90, -15, -165] },
      { kind: "rule", layer: "body", r: 33.4, width: 0.9, colour: "#6b4510" },
      { kind: "field", layer: "body", r: 31, a: "#4a2f80", b: "#120a26", ink: "#1c1028",
        wire: { inset: 2.6, width: 0.9, colour: "#e8c25e" } },
      { kind: "gems", layer: "top", r: 36.2, angles: [-90, -15, -165], size: 3.1, sizes: [3.8, 3.1, 3.1],
        colour: "#9a5ce0", shade: "#3a1670", light: "#efdcff", bezel: "#f2cf6a", ink: "#1c1028" },
    ],
    emblem: { kind: "keys", y: 45, scale: 0.86, crown: true, gem: "#b07af0",
      metal: "#f6d57c", shade: "#b27a22", ink: "#160d20", hole: "#170d2e" },
    textSize: 17, textColour: "#f6d87e", textOutline: "rgba(20,8,40,0.75)", textTracking: 0.8,
    textFitWidth: 58,
    ribbon: {
      colour: "#4b2a80", shade: "#24123f", y: 76, halfWidth: 37, height: 18,
      tail: 9, curve: 2.5, stroke: "#e2bb58",
    },
  },

  medalSky: {
    id: "medalSky", name: "Sky Medallion",
    shape: "shieldRound", shapeParams: { shoulder: 0.88, point: 0.35, taper: 0.28, round: 0.5 },
    fill: { kind: "radial", a: "#8fd4ff", b: "#2a4a7a" },
    ring: { colour: "#f2c14e", width: 3 }, bevel: 0.5,
    glow: { colour: "#8fd4ff", strength: 0.6, pulseSpeed: 2.2, pulseDepth: 0.55 },
    tier: "gold", iconScale: 0.56, textSize: 30, textColour: "#fff8e2", textOutline: "rgba(0,20,45,0.5)",
  },

  // -------------------------------------------------------------------------
  //  LANDMARK STYLES — one emblem per milestone, deliberately NOT a ladder
  // -------------------------------------------------------------------------
  /**
   * THE LANDMARKS. The `t1`..`t5` styles above are uniform ON PURPOSE: they are rungs, and a player must be
   * able to read "tier 3" off any of them without knowing which species earned it. These styles are the
   * OPPOSITE case. Each one marks a single thing the player did once — bought their fiftieth plot, filled the
   * greenhouse, kept a month of check-ins — and there is no rung above or below it to match. So each gets its
   * own silhouette and its own colour, and a case full of them reads as a shelf of individual achievements rather
   * than a stack of the same emblem five times.
   *
   * HOW THEY ARE KEPT APART, since "make them all different" is not a rule a future editor can apply:
   *   • SUBJECT OWNS COLOUR. Land is earth-brown (`plotEarth`/`plotStone`/`plotEstate`/`plotGold`), the
   *     greenhouse is glass-green (`glassPane`/`glassGable`/`glassCrown`), water is pond-teal
   *     (`waterTeal`/`waterLily`/`waterDeep`), decor is lantern-warm (`hearthAmber`/`terracottaStar`/
   *     `verdigris`), the calendar is dusk-violet (`dawnViolet`/`duskAmethyst`/`moonPearl`), money is
   *     coin-gold (`coinStack`/`coinBanner`/`vaultAmber`), and the three skill tracks keep their HUD colours
   *     (green / blue / amber). Two achievements from different families can therefore never be confused at case
   *     size even when the shapes rhyme.
   *   • WITHIN a family the SHAPE escalates, the hue does not: hexagon → shield → gem → banner for plots,
   *     pane → gable → crown for glass. That is the one place these behave like a ladder, because a family of
   *     three IS one. EVERY achievement still names its own style — no two of these milestones share an emblem, so
   *     a case of them never shows the same picture twice with a different number on it.
   *   • The CAPSTONES (`plotGold`, `glassCrown`, `vaultAmber`) are the only landmarks allowed a 1.0-strength
   *     fast pulse. Reserve it: it is what makes a completion read as finished across a wall of badges.
   */

  /** Plots I — a surveyor's marker. Earth and turf: a hexagonal claim stone with a grass-green rim. */
  plotEarth: {
    id: "plotEarth", name: "Earthwork Hexagon",
    shape: "hexagon", shapeParams: { rotate: 30, round: 0.2 },
    fill: { kind: "linear", a: "#a2764a", b: "#4a3218", angle: 0 },
    ring: { colour: "#8fbf63", width: 2.5 }, bevel: 0.36,
    glow: { colour: "#8fbf63", strength: 0.32, pulseSpeed: 0, pulseDepth: 0.4 },
    tier: "bronze", iconScale: 0.6, textSize: 24, textColour: "#fff0d8", textOutline: "rgba(0,0,0,0.55)",
  },
  /** Plots II — the same claim in cut stone. A pale granite shield: the garden has outgrown a wooden peg. */
  plotStone: {
    id: "plotStone", name: "Granite Bound",
    shape: "shield", shapeParams: { shoulder: 0.94, point: 0.48, taper: 0.14 },
    fill: { kind: "linear", a: "#b9bfae", b: "#5d6354", angle: 0 },
    ring: { colour: "#e8ecdf", width: 3 }, bevel: 0.42,
    glow: { colour: "#cfd8bd", strength: 0.45, pulseSpeed: 3.4, pulseDepth: 0.4 },
    tier: "silver", iconScale: 0.58, textSize: 24, textColour: "#2b3125", textOutline: "rgba(255,255,255,0.5)",
    textY: 62,
  },
  /** Plots III — an estate. The granite family's grey warmed with ivy-green and cut as a tall gem: land that
   *  has been held long enough to be worth something, one step below the completion. */
  plotEstate: {
    id: "plotEstate", name: "Estate Bound",
    shape: "gem", shapeParams: { shoulder: 0.82, point: 0.6, round: 0.15 },
    fill: { kind: "linear", a: "#cdd4be", b: "#3e4a32", angle: 0 },
    ring: { colour: "#f0f4e4", width: 3.5 }, bevel: 0.48,
    glow: { colour: "#a8c47e", strength: 0.66, pulseSpeed: 2.5, pulseDepth: 0.5 },
    tier: "gold", iconScale: 0.72, iconOffsetY: -0.08,
    textSize: 20, textColour: "#28311d", textOutline: "rgba(245,250,230,0.6)", textY: 80,
  },
  /**
   * Plots IV — THE COMPLETION. The 50-plot garden, which is the whole map bought.
   *
   * A BANNER, not a shield: the swallowtail ribbon is the only silhouette in the library that reads as a
   * claim STAKED rather than a boundary marked, and nothing else in the milestone set uses it as a capstone.
   * `point: 0.72` is not decoration — the notch has to bite most of the ribbon height or the tails read as a
   * dent rather than a swallowtail, which is the same trap the shape builder's own header records.
   *
   * NO CREST, AND THAT IS THE DESIGN. Three were tried and all three failed the same way. The banner offers
   * the NARROWEST usable span in the library (0.38 against a circle's 0.65), and every tall landmark in the
   * decor roster is a thin-lined pale object: the obelisk rendered as a vertical sliver, and the garden arch
   * dissolved into the gold field outright. So the NUMBER is the subject here, set large and sat on the flat
   * centre band where the ribbon is widest. `founder` reached the same conclusion from the other direction —
   * when a shape cannot hold a picture, give it a word. Do not re-add an icon without checking it at 38px.
   *
   * Gold on deep earth, and the only plot badge with a full-strength pulse — the signal this family is done.
   */
  plotGold: {
    id: "plotGold", name: "Whole Garden Standard",
    shape: "banner", shapeParams: { shoulder: 0.96, point: 0.72, taper: 0.12 },
    fill: { kind: "linear", a: "#f0c552", b: "#6b4413", angle: 0 },
    ring: { colour: "#fff2c4", width: 3.5 }, bevel: 0.5,
    glow: { colour: "#ffd76a", strength: 1, pulseSpeed: 1.8, pulseDepth: 0.6 },
    tier: "gold",
    textSize: 32, textColour: "#4a2f05", textOutline: "rgba(255,244,205,0.6)", textY: 54, textFitWidth: 54,
  },
  /** THE COMPOSTER I — the first compost. A round shield of dark loam with a leaf-green ring: turned earth. */
  compostLoam: {
    id: "compostLoam", name: "Turned Loam",
    shape: "shieldRound",
    fill: { kind: "linear", a: "#6d4a2c", b: "#2a1a0e", angle: 0 },
    ring: { colour: "#8fbf63", width: 2.5 }, bevel: 0.38,
    glow: { colour: "#8fbf63", strength: 0.3, pulseSpeed: 0, pulseDepth: 0.4 },
    tier: "bronze", iconScale: 0.6,
  },
  /** THE COMPOSTER II — a hundred composts. The loam cut as a hexagon of pressed humus, straw-ringed. */
  compostTurned: {
    id: "compostTurned", name: "Heap Keeper",
    shape: "hexagon", shapeParams: { rotate: 0, round: 0.18 },
    fill: { kind: "linear", a: "#7d5634", b: "#3b2616", angle: 0 },
    ring: { colour: "#d9b56a", width: 3 }, bevel: 0.42,
    glow: { colour: "#d9b56a", strength: 0.4, pulseSpeed: 2.6, pulseDepth: 0.4 },
    tier: "silver", textSize: 28, textColour: "#fff0d8", textOutline: "rgba(0,0,0,0.55)", textFitWidth: 56,
  },
  /** THE COMPOSTER III — a rack of good soil. A leaf of rich humus: the compost has become a garden. */
  compostHumus: {
    id: "compostHumus", name: "Rich Humus",
    shape: "leaf", shapeParams: { point: 0.8, taper: 0.5, rotate: 0 },
    fill: { kind: "linear", a: "#4f6d2c", b: "#2a1a0e", angle: 0 },
    ring: { colour: "#b7e07a", width: 3 }, bevel: 0.44,
    glow: { colour: "#b7e07a", strength: 0.5, pulseSpeed: 2.2, pulseDepth: 0.45 },
    tier: "silver", iconScale: 0.56,
  },
  /** THE COMPOSTER IV — the first ★★★★★. A gold star over black earth: black gold. */
  compostGold: {
    id: "compostGold", name: "Black Gold",
    shape: "star", shapeParams: { points: 5, inner: 0.5, rotate: 0 },
    fill: { kind: "linear", a: "#f0c552", b: "#2a1a0e", angle: 0 },
    ring: { colour: "#fff2c4", width: 3 }, bevel: 0.5,
    glow: { colour: "#ffd76a", strength: 1, pulseSpeed: 1.8, pulseDepth: 0.6 },
    tier: "gold", textSize: 22, textColour: "#2a1a0e", textOutline: "rgba(255,244,205,0.6)", textY: 60,
  },

  /**
   * Shelves I — a pane of greenhouse glass. Cold and pale: the palest fill in the landmark set.
   *
   * A WIDE, SHALLOW ROUND SHIELD, not the hexagon it started as — a flat-top hexagon is byte-identical to the
   * shipped `hexWood` (the `round` slider does nothing on this family; the builder reads only `rotate`), so
   * the Bug Collector badge and this one would have been the same emblem in two colours. `shoulder: 0.98` with
   * `point: 0.12` is the flattest, widest silhouette the library can make, which is what a pane of glass is.
   */
  glassPane: {
    id: "glassPane", name: "Glass Pane",
    shape: "shieldRound", shapeParams: { shoulder: 0.98, point: 0.12, taper: 0.04, round: 0.2 },
    fill: { kind: "linear", a: "#a8e0d0", b: "#20514c", angle: 0 },
    ring: { colour: "#dff6ee", width: 2.5 }, bevel: 0.48,
    glow: { colour: "#8fe0c8", strength: 0.4, pulseSpeed: 3, pulseDepth: 0.45 },
    tier: "silver", iconScale: 0.88, iconOffsetY: -0.06,
    textSize: 24, textColour: "#f2fffa", textOutline: "rgba(0,35,30,0.55)", textY: 74,
  },
  /** Shelves II — three racks. A gabled shield: the greenhouse ROOF line, still glass-green but no longer a
   *  single pane. Reads as a building where `glassPane` reads as a part. */
  glassGable: {
    id: "glassGable", name: "Glass Gable",
    shape: "shield", shapeParams: { shoulder: 0.66, point: 0.28, taper: 0.06 },
    fill: { kind: "linear", a: "#8fd8c4", b: "#194a44", angle: 0 },
    ring: { colour: "#d2f2e8", width: 3 }, bevel: 0.45,
    glow: { colour: "#7fd8bc", strength: 0.55, pulseSpeed: 2.8, pulseDepth: 0.48 },
    tier: "silver", iconScale: 0.8, iconOffsetY: -0.02,
    textSize: 23, textColour: "#f2fffa", textOutline: "rgba(0,35,30,0.55)", textY: 64,
  },
  /** Shelves III — THE COMPLETION. All five racks: a spiked star in glasshouse green, pulsing at full. */
  glassCrown: {
    id: "glassCrown", name: "Glasshouse Crown",
    shape: "star", shapeParams: { points: 8, inner: 0.7, rotate: 22.5 },
    fill: { kind: "radial", a: "#d8ffe8", b: "#1e6b52" },
    ring: { colour: "#f0fff8", width: 3 }, bevel: 0.52,
    glow: { colour: "#7dffc0", strength: 1, pulseSpeed: 1.9, pulseDepth: 0.62 },
    tier: "gold", iconScale: 0.68, textSize: 24, textColour: "#0d3a2c", textOutline: "rgba(230,255,245,0.7)",
    textFitWidth: 44,
  },

  /** Water I — a teardrop. The only teardrop in the set, so "this achievement is about water" needs no reading. */
  waterTeal: {
    id: "waterTeal", name: "Still Water",
    shape: "teardrop", shapeParams: { point: 0.8, rotate: 0 },
    fill: { kind: "radial", a: "#7fd8ef", b: "#16495e" },
    ring: { colour: "#cdf0fa", width: 2.5 }, bevel: 0.46,
    glow: { colour: "#5fc8e8", strength: 0.5, pulseSpeed: 3.6, pulseDepth: 0.5 },
    tier: "silver", iconScale: 0.52, iconOffsetY: 0.06,
    textSize: 22, textColour: "#eafaff", textOutline: "rgba(0,30,45,0.55)", textY: 82,
  },
  /**
   * Water II — a settled pond. A soft-cornered HEXAGON in pond-teal, turned 30° off `glassPane`'s flat-top
   * orientation so the two round hexagons never read as the same emblem.
   *
   * NOT A LILY PAD, though that was the first idea: the `leaf` path builder IGNORES `rotate` (it only reads
   * `point` and `taper`), so a leaf can only ever grow upward and a "pad laid flat" is not expressible. A
   * leaf here would have been silhouette-identical to `leafGreen`, which is the one thing these styles exist
   * to avoid. Do not "fix" this by adding rotation to the leaf builder — `leafGreen` ships on that path and
   * its art would move.
   */
  waterLily: {
    id: "waterLily", name: "Pond Scallop",
    shape: "rosette", shapeParams: { points: 7, inner: 0.72 },
    fill: { kind: "radial", a: "#8fe4e0", b: "#124a52" },
    ring: { colour: "#ddf6f8", width: 2.5 }, bevel: 0.42,
    glow: { colour: "#56cfd4", strength: 0.6, pulseSpeed: 3, pulseDepth: 0.5 },
    tier: "silver", iconScale: 0.58, textSize: 22, textColour: "#eafeff",
    textOutline: "rgba(0,30,40,0.55)", textFitWidth: 52,
  },
  /** Water III — deep water. A slow twelve-point rosette, like ripple rings; the darkest fill in the set. */
  waterDeep: {
    id: "waterDeep", name: "Deep Water Rosette",
    shape: "rosette", shapeParams: { points: 12, inner: 0.84 },
    fill: { kind: "radial", a: "#6ad4e8", b: "#0d2f45" },
    ring: { colour: "#b6ecf8", width: 2.5 }, bevel: 0.44,
    glow: { colour: "#49c2e0", strength: 0.72, pulseSpeed: 2.6, pulseDepth: 0.55 },
    tier: "gold", iconScale: 0.6, textSize: 22, textColour: "#eafaff", textOutline: "rgba(0,25,40,0.6)",
    textFitWidth: 48,
  },

  /** Decor I — a warm wooden gem. The garden furnished: lantern-amber against the cool badges around it. */
  hearthAmber: {
    id: "hearthAmber", name: "Hearthlight Gem",
    shape: "gem", shapeParams: { shoulder: 0.76, point: 0.62, round: 0.15 },
    fill: { kind: "radial", a: "#ffcf7d", b: "#8a4a14" },
    ring: { colour: "#ffe8bd", width: 3 }, bevel: 0.46,
    glow: { colour: "#ffb44a", strength: 0.58, pulseSpeed: 2.8, pulseDepth: 0.5 },
    tier: "gold", iconScale: 0.74, iconOffsetY: -0.08,
    textSize: 19, textColour: "#4a2606", textOutline: "rgba(255,235,200,0.6)", textY: 80,
  },
  /** Decor II — a garden laid out. A five-point star in warm terracotta: the same lantern-warmth as
   *  `hearthAmber` but an open radiating silhouette, for the achievement about spreading things across the ground. */
  terracottaStar: {
    id: "terracottaStar", name: "Terracotta Star",
    shape: "star", shapeParams: { points: 5, inner: 0.52, rotate: 0 },
    fill: { kind: "linear", a: "#f0a96a", b: "#76391a", angle: 0 },
    ring: { colour: "#ffdcb4", width: 3 }, bevel: 0.42,
    glow: { colour: "#ff9f4a", strength: 0.6, pulseSpeed: 2.6, pulseDepth: 0.5 },
    tier: "gold", iconScale: 0.6, textSize: 24, textColour: "#4a2306",
    textOutline: "rgba(255,230,195,0.65)", textFitWidth: 42,
  },
  /** Decor III — THE FURNISHED GARDEN. A wide round shield in weathered copper-verdigris: aged, settled, lived in. */
  verdigris: {
    id: "verdigris", name: "Weathered Verdigris",
    shape: "shieldRound", shapeParams: { shoulder: 0.94, point: 0.3, taper: 0.2, round: 0.7 },
    fill: { kind: "linear", a: "#7fd4b8", b: "#2a5c52", angle: 0 },
    ring: { colour: "#c98b3e", width: 3.5 }, bevel: 0.44,
    glow: { colour: "#6fd0b0", strength: 0.62, pulseSpeed: 2.4, pulseDepth: 0.5 },
    tier: "gold", iconScale: 0.86, iconOffsetY: -0.02,
    textSize: 22, textColour: "#f0fff9", textOutline: "rgba(0,35,30,0.6)", textY: 76,
  },

  /** Streak I — first light. A dawn-violet teardrop turned into a rising sun by the rotation. */
  dawnViolet: {
    id: "dawnViolet", name: "Dawn Sigil",
    shape: "shieldRound", shapeParams: { shoulder: 0.8, point: 0.2, taper: 0.36, round: 0.9 },
    fill: { kind: "linear", a: "#c8a8f0", b: "#3b2560", angle: 0 },
    ring: { colour: "#ecdcff", width: 2.5 }, bevel: 0.42,
    glow: { colour: "#b48cf0", strength: 0.48, pulseSpeed: 3.2, pulseDepth: 0.5 },
    tier: "silver", iconScale: 0.54, textSize: 26, textColour: "#f6eeff", textOutline: "rgba(30,10,55,0.6)",
  },
  /** Streak II — a fortnight. A violet gem: harder and colder than `dawnViolet`'s soft dome, which is the
   *  point — two unbroken weeks is a discipline, not a first morning. */
  duskAmethyst: {
    id: "duskAmethyst", name: "Dusk Amethyst",
    shape: "gem", shapeParams: { shoulder: 0.74, point: 0.72, round: 0.15 },
    fill: { kind: "radial", a: "#e0c8ff", b: "#432578" },
    ring: { colour: "#f2e6ff", width: 3 }, bevel: 0.5,
    glow: { colour: "#a87cf0", strength: 0.66, pulseSpeed: 2.6, pulseDepth: 0.55 },
    tier: "gold", iconScale: 0.72, iconOffsetY: -0.08,
    textSize: 20, textColour: "#f8f2ff", textOutline: "rgba(30,10,55,0.6)", textY: 80,
  },
  /** Streak III — a full calendar. Pearl-white on a cool hexagon: the moon's own colour, for a month of visits. */
  moonPearl: {
    id: "moonPearl", name: "Moon Pearl",
    shape: "circle",
    fill: { kind: "radial", a: "#fdfbff", b: "#6c6f96" },
    ring: { colour: "#d5d8f2", width: 3 }, bevel: 0.55,
    glow: { colour: "#e6e8ff", strength: 0.75, pulseSpeed: 2.2, pulseDepth: 0.55 },
    tier: "gold", iconScale: 0.58, textSize: 28, textColour: "#2e3150", textOutline: "rgba(255,255,255,0.6)",
    textFitWidth: 52,
  },

  /**
   * Coin I — a struck coin. No crest: the NUMBER is the subject, so this is the only style in the set built
   * purely to carry text, and it is why `textFitWidth` is the widest here (54) — "100K" must not condense.
   *
   * MILLED EDGE, NOT A PLAIN DISC. A bare circle already belongs to `moonPearl`, so this one is a rosette at
   * inner 0.94 — geometrically a circle with a finely notched rim, which is exactly what the edge of a coin
   * is, and the one thing that tells the two apart at 38px. 16 is the point count, because 16 is the CAP the
   * shape builder clamps `points` to; asking for more silently gets you 16. Do not flatten `inner` toward
   * 0.8 — it turns the milling into visible teeth and the coin becomes a gear.
   */
  coinStack: {
    id: "coinStack", name: "Struck Coin",
    shape: "rosette", shapeParams: { points: 16, inner: 0.94 },
    fill: { kind: "linear", a: "#ffd978", b: "#9c6a17", angle: 0 },
    ring: { colour: "#fff0bd", width: 3.5 }, bevel: 0.5,
    glow: { colour: "#ffc94a", strength: 0.42, pulseSpeed: 0, pulseDepth: 0.4 },
    tier: "silver", iconScale: 0.56, textSize: 28, textColour: "#4a2f05", textOutline: "rgba(255,244,205,0.6)",
    textFitWidth: 54,
  },
  /** Coin II — a treasury. The coin's gold on a banner: money that has stopped being loose change. */
  coinBanner: {
    id: "coinBanner", name: "Treasury Banner",
    shape: "banner", shapeParams: { shoulder: 0.9, point: 0.66, taper: 0.16 },
    fill: { kind: "linear", a: "#ffe19a", b: "#8a5c10", angle: 0 },
    ring: { colour: "#fff3c4", width: 3 }, bevel: 0.46,
    glow: { colour: "#ffc94a", strength: 0.58, pulseSpeed: 2.8, pulseDepth: 0.48 },
    tier: "gold", iconScale: 0.5, iconOffsetY: -0.12,
    textSize: 26, textColour: "#4a2f05", textOutline: "rgba(255,244,205,0.6)", textY: 56, textFitWidth: 56,
  },
  /** Coin III — THE VAULT. Deep amber gem at full pulse; the wealth capstone. */
  vaultAmber: {
    id: "vaultAmber", name: "Vault Amber",
    shape: "gem", shapeParams: { shoulder: 0.8, point: 0.78, round: 0.15 },
    fill: { kind: "radial", a: "#ffe9a8", b: "#7a3f06" },
    ring: { colour: "#fff6d8", width: 3.5 }, bevel: 0.52,
    glow: { colour: "#ffb01f", strength: 1, pulseSpeed: 1.7, pulseDepth: 0.62 },
    tier: "gold", iconScale: 0.72, iconOffsetY: -0.08,
    textSize: 26, textColour: "#4a2603", textOutline: "rgba(255,240,200,0.65)", textY: 56, textFitWidth: 46,
  },
  /**
   * Petals — the premium currency. A NARROW leaf: `taper: 0.3` is well off `leafGreen`'s 0.45, so this reads
   * as a single long petal where that one reads as foliage. The distinction has to come from `point`/`taper`
   * alone, because the leaf builder ignores `rotate` (see `waterLily`).
   *
   * Violet, not green, and the only mythic tier outside the t5 rosette — petals are bought, not grown, and
   * this must never be mistaken for an ordinary gardening achievement.
   */
  petalMythic: {
    id: "petalMythic", name: "Petal Sigil",
    shape: "leaf", shapeParams: { point: 0.98, taper: 0.3 },
    fill: { kind: "radial", a: "#ffc8f0", b: "#631f6e" },
    ring: { colour: "#ffe0f8", width: 2.5 }, bevel: 0.46,
    glow: { colour: "#f08ce0", strength: 0.8, pulseSpeed: 2.1, pulseDepth: 0.58 },
    tier: "mythic", iconScale: 0.5, textSize: 22, textColour: "#fff2fc", textOutline: "rgba(55,5,60,0.6)",
    textY: 57, textFitWidth: 38,
  },

  /**
   * THE THREE SKILL-TRACK CAPSTONES. Same silhouette, one colour each — and that sameness is deliberate
   * here where it is forbidden elsewhere: Gardening, Fishing and Naturalist ARE parallel tracks, the HUD
   * already colours them green / blue / amber, and three matching stars read instantly as "one of each".
   * A rank NUMBER is the subject, so the star is sized for text rather than a crest.
   */
  trackGreen: {
    id: "trackGreen", name: "Gardening Track Star",
    shape: "star", shapeParams: { points: 7, inner: 0.58, rotate: 0 },
    fill: { kind: "radial", a: "#d6f5a8", b: "#2c6b24" },
    ring: { colour: "#eeffd4", width: 3 }, bevel: 0.5,
    glow: { colour: "#9be36a", strength: 0.85, pulseSpeed: 2, pulseDepth: 0.58 },
    tier: "gold", iconScale: 0.62, textSize: 30, textColour: "#1c4416",
    textOutline: "rgba(240,255,220,0.7)", textFitWidth: 42,
  },
  trackBlue: {
    id: "trackBlue", name: "Fishing Track Star",
    shape: "star", shapeParams: { points: 7, inner: 0.58, rotate: 0 },
    fill: { kind: "radial", a: "#c4ecff", b: "#1b4c78" },
    ring: { colour: "#e6f7ff", width: 3 }, bevel: 0.5,
    glow: { colour: "#5bb7ea", strength: 0.85, pulseSpeed: 2, pulseDepth: 0.58 },
    tier: "gold", iconScale: 0.62, textSize: 30, textColour: "#0f3253",
    textOutline: "rgba(230,247,255,0.7)", textFitWidth: 42,
  },
  trackAmber: {
    id: "trackAmber", name: "Bug Catching Track Star",
    shape: "star", shapeParams: { points: 7, inner: 0.58, rotate: 0 },
    fill: { kind: "radial", a: "#ffe6b0", b: "#8a5410" },
    ring: { colour: "#fff4dc", width: 3 }, bevel: 0.5,
    glow: { colour: "#f2c14e", strength: 0.85, pulseSpeed: 2, pulseDepth: 0.58 },
    tier: "gold", iconScale: 0.62, textSize: 30, textColour: "#51300a",
    textOutline: "rgba(255,244,220,0.7)", textFitWidth: 42,
  },

  /** The naturalist's cabinet — a wood-and-brass hexagon for the "collected every kind of thing" achievements.
   *  Warmer and redder than `hexWood` (which the Bug Collector achievement already uses) so the two do not twin. */
  cabinetBrass: {
    id: "cabinetBrass", name: "Collector's Cabinet",
    shape: "hexagon", shapeParams: { rotate: 15, round: 0.3 },
    fill: { kind: "linear", a: "#c98f52", b: "#4a2412", angle: 0 },
    ring: { colour: "#f2d9a0", width: 3.5 }, bevel: 0.44,
    glow: { colour: "#e8b25a", strength: 0.55, pulseSpeed: 2.6, pulseDepth: 0.5 },
    tier: "gold", iconScale: 0.62, textSize: 24, textColour: "#fff2da", textOutline: "rgba(35,15,5,0.6)",
  },

  /**
   * THE THREE ROSTER COMPLETIONS — every fish, every insect, every plant. Each is a 10-point capstone and
   * each gets its own emblem, but they are built on ONE silhouette family (a deep rosette) for the same
   * reason the track stars are: a player who owns all three should be able to see at a glance that they are
   * the same kind of achievement. Colour carries the subject — aquarium blue, chitin bronze-violet, herbarium
   * gold-green — and all three run the full-strength pulse a completion is entitled to.
   */
  aquariumGlass: {
    id: "aquariumGlass", name: "Filled Aquarium",
    shape: "rosette", shapeParams: { points: 10, inner: 0.72 },
    fill: { kind: "radial", a: "#bdf0ff", b: "#103a63" },
    ring: { colour: "#e4f8ff", width: 3.5 }, bevel: 0.52,
    glow: { colour: "#54cbf5", strength: 1, pulseSpeed: 1.8, pulseDepth: 0.6 },
    tier: "mythic", iconScale: 0.66, textSize: 22, textColour: "#f0fbff",
    textOutline: "rgba(0,25,45,0.6)", textFitWidth: 46,
  },
  chitinCase: {
    id: "chitinCase", name: "Filled Case",
    shape: "rosette", shapeParams: { points: 10, inner: 0.72 },
    fill: { kind: "radial", a: "#f0c89a", b: "#4a2455" },
    ring: { colour: "#fbe4c8", width: 3.5 }, bevel: 0.52,
    glow: { colour: "#d8a0e8", strength: 1, pulseSpeed: 1.8, pulseDepth: 0.6 },
    tier: "mythic", iconScale: 0.66, textSize: 22, textColour: "#fff4e8",
    textOutline: "rgba(40,10,50,0.6)", textFitWidth: 46,
  },
  herbarium: {
    id: "herbarium", name: "Complete Herbarium",
    shape: "rosette", shapeParams: { points: 10, inner: 0.72 },
    fill: { kind: "radial", a: "#f4f0b8", b: "#2a5c22" },
    ring: { colour: "#fffce0", width: 3.5 }, bevel: 0.52,
    glow: { colour: "#d4e86a", strength: 1, pulseSpeed: 1.8, pulseDepth: 0.6 },
    tier: "mythic", iconScale: 0.66, textSize: 22, textColour: "#1e4419",
    textOutline: "rgba(250,255,220,0.7)", textFitWidth: 46,
  },
};

/** A blank style, used as the "＋ New style" starting point in the designer. */
export function newBadgeStyle(id: string): BadgeStyle {
  return {
    id,
    name: "New Style",
    shape: "shield",
    shapeParams: { ...SHAPE_DEFAULTS.shield },
    fill: { kind: "linear", a: "#4f9a34", b: "#2a5c21", angle: 0 },
    ring: { colour: "#f2c14e", width: 3 },
    bevel: 0.35,
    glow: { colour: "#7bc24a", strength: 0.4, pulseSpeed: 2.4, pulseDepth: 0.5 },
    tier: "bronze",
    iconScale: 0.55,
    textSize: 22,
    textColour: "#f8f3e6",
    textOutline: "rgba(0,0,0,0.55)",
  };
}

/**
 * Coerce anything loaded from the DB into a safe BadgeStyle. The tuning blob is hand-editable and survives
 * deploys, so a style WILL eventually arrive malformed. Everything falls back rather than throwing, because
 * one bad style must never take the profile page down.
 */
export function normaliseStyle(raw: unknown, base: BadgeStyle | undefined, id: string): BadgeStyle {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const fallback = base ?? newBadgeStyle(id);
  const shape: BadgeShapeId = BADGE_SHAPE_IDS.includes(o.shape as BadgeShapeId)
    ? (o.shape as BadgeShapeId) : fallback.shape;
  const fr = (o.fill && typeof o.fill === "object" ? o.fill : {}) as Record<string, unknown>;
  const ringRaw = (o.ring && typeof o.ring === "object" ? o.ring : null) as Record<string, unknown> | null;
  const glowRaw = (o.glow && typeof o.glow === "object" ? o.glow : null) as Record<string, unknown> | null;
  const num = (v: unknown, d: number | undefined): number | undefined =>
    typeof v === "number" && Number.isFinite(v) ? v : d;
  const str = (v: unknown, d: string | undefined): string | undefined => (typeof v === "string" ? v : d);
  return {
    id,
    name: str(o.name, fallback.name) || id,
    shape,
    shapeParams: (o.shapeParams && typeof o.shapeParams === "object"
      ? o.shapeParams : fallback.shapeParams ?? SHAPE_DEFAULTS[shape]) as BadgeShapeParams,
    fill: {
      kind: fr.kind === "linear" || fr.kind === "radial" || fr.kind === "flat"
        ? fr.kind : fallback.fill.kind,
      a: str(fr.a, fallback.fill.a)!,
      b: str(fr.b, fallback.fill.b),
      angle: num(fr.angle, fallback.fill.angle ?? 0),
    },
    ring: ringRaw
      ? { colour: str(ringRaw.colour, "#f2c14e")!, width: num(ringRaw.width, 3)! }
      : fallback.ring,
    bevel: num(o.bevel, fallback.bevel ?? 0.35),
    glow: glowRaw
      ? {
          colour: str(glowRaw.colour, "#7bc24a")!,
          strength: num(glowRaw.strength, 0.4)!,
          pulseSpeed: num(glowRaw.pulseSpeed, 0),
          pulseDepth: num(glowRaw.pulseDepth, 0.5),
        }
      : fallback.glow,
    tier: ["bronze", "silver", "gold", "mythic"].includes(String(o.tier))
      ? (o.tier as BadgeTier) : fallback.tier,
    iconScale: num(o.iconScale, fallback.iconScale ?? 0.55),
    iconOffsetY: num(o.iconOffsetY, fallback.iconOffsetY ?? 0),
    textSize: num(o.textSize, fallback.textSize ?? 22),
    textColour: str(o.textColour, fallback.textColour ?? "#f8f3e6"),
    textOutline: str(o.textOutline, fallback.textOutline),
    textY: num(o.textY, fallback.textY),
    textTracking: num(o.textTracking, fallback.textTracking),
    textFitWidth: num(o.textFitWidth, fallback.textFitWidth),
    defaultIcon: str(o.defaultIcon, fallback.defaultIcon),
    ribbon: (o.ribbon && typeof o.ribbon === "object" ? o.ribbon : fallback.ribbon) as BadgeStyle["ribbon"],
    outline: normaliseOutline(o.outline) ?? fallback.outline,
    ornaments: Array.isArray(o.ornaments)
      ? (o.ornaments as unknown[]).filter((x): x is BadgeOrnament => !!x && typeof x === "object"
          && ORNAMENT_KINDS.includes(String((x as { kind?: unknown }).kind)))
      : fallback.ornaments,
    ornamentCy: num(o.ornamentCy, fallback.ornamentCy),
    emblem: o.emblem && typeof o.emblem === "object" && (o.emblem as { kind?: unknown }).kind === "keys"
      ? (o.emblem as BadgeEmblem) : fallback.emblem,
  };
}

/** The ornament kinds BadgeArt can draw — a stored layer of any other kind is dropped, never guessed at. */
const ORNAMENT_KINDS: string[] = ["rays", "beads", "rule", "scroll", "field", "gems"];

/** A stored outline, or undefined when it is missing or malformed (rings must be {colour, width>0}). */
function normaliseOutline(raw: unknown): BadgeOutline | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const o = raw as Record<string, unknown>;
  if (!Array.isArray(o.rings)) return undefined;
  const rings = o.rings
    .filter((r): r is { colour: string; width: number } => !!r && typeof r === "object"
      && typeof (r as { colour?: unknown }).colour === "string"
      && typeof (r as { width?: unknown }).width === "number" && (r as { width: number }).width > 0)
    .map((r) => ({ colour: r.colour, width: Math.min(12, r.width) }));
  if (!rings.length) return undefined;
  return {
    rings,
    ink: typeof o.ink === "string" ? o.ink : undefined,
    inkWidth: typeof o.inkWidth === "number" && o.inkWidth >= 0 ? Math.min(4, o.inkWidth) : undefined,
  };
}

// ---------------------------------------------------------------------------
//  LIVE OVERLAY + RESOLUTION
// ---------------------------------------------------------------------------

/** Live per-id style overrides from the `badgeStyles` tuning key. A `null` entry deletes a style. */
let LIVE_STYLES: Record<string, BadgeStyle> = {};
let DEAD_STYLES = new Set<string>();

/** Install the live style overlay (called at boot + on every tuning refresh; see main.tsx). */
export function setBadgeStyleOverrides(o: Record<string, unknown> | null | undefined): void {
  const next: Record<string, BadgeStyle> = {};
  const dead = new Set<string>();
  if (o && typeof o === "object") {
    for (const [id, raw] of Object.entries(o)) {
      if (raw === null) { dead.add(id); continue; }
      next[id] = normaliseStyle(raw, DEFAULT_BADGE_STYLES[id], id);
    }
  }
  LIVE_STYLES = next;
  DEAD_STYLES = dead;
}

/** Every style the game knows about: source defaults + live overlay, minus deletions. */
export function allBadgeStyles(): Record<string, BadgeStyle> {
  const out: Record<string, BadgeStyle> = { ...DEFAULT_BADGE_STYLES, ...LIVE_STYLES };
  for (const id of DEAD_STYLES) delete out[id];
  return out;
}

/** One style by id. Falls back to the FIRST available style rather than undefined, so an achievement naming a
 *  style that was deleted still draws something instead of vanishing from the player's case. */
export function badgeStyle(id: string | undefined): BadgeStyle | undefined {
  if (id && !DEAD_STYLES.has(id)) {
    const s = LIVE_STYLES[id] ?? DEFAULT_BADGE_STYLES[id];
    if (s) return s;
  }
  return undefined;
}

/**
 * Resolve a badge reference into everything <BadgeArt> needs to draw. This is the ONE place a style and its
 * content are combined, so the pop-up, the case and the designer preview can never disagree.
 *
 * Returns undefined only when the named style does not exist AND no fallback is available — the caller
 * treats that as "no badge", which is why a missing style degrades to the achievement's emoji rather than a crash.
 */
export function resolveBadge(
  ref: BadgeRef | undefined,
  meta: { id: string; name: string },
): ResolvedBadge | undefined {
  if (!ref) return undefined;
  const style = badgeStyle(ref.style) ?? Object.values(allBadgeStyles())[0];
  if (!style) return undefined;
  return {
    id: meta.id,
    name: meta.name,
    style,
    icon: parseIconRef(ref.icon ?? style.defaultIcon),
    text: ref.text,
    iconScale: ref.iconScale ?? style.iconScale ?? 0.55,
    iconOffsetY: ref.iconOffsetY ?? style.iconOffsetY ?? 0,
  };
}

export const BADGE_STYLE_IDS = (): string[] => Object.keys(allBadgeStyles());
